import React, { useEffect, useRef, useState } from "react";
import { Button, Input, LoginWrapper, SVG } from "../../shared";
import "./RegisterPage.scss";
import { Field, Formik, Form } from "formik";
import { ROUTE_ENUM, SVG_ENUM, VALIDATION_ENUM } from "../../enums";
import * as Yup from "yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useGetMe,
  useVerifyRegistrationToken,
  useActivateWithPassword,
} from "../../hooks/auth";
import { checkIfLogged } from "../../utils";

type RegisterPageProps = {};

const RegisterPage: React.FC<RegisterPageProps> = () => {
  const navigate = useNavigate();
  const formRef = useRef<any>(null);
  const [searchParams] = useSearchParams();
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [tokenValid, setTokenValid] = useState(false);
  const [userInfo, setUserInfo] = useState<{
    name: string;
    email: string;
  } | null>(null);
  const [isVerifying, setIsVerifying] = useState(true);

  const { data: authorizedUser } = useGetMe();
  const token = searchParams.get("token");

  const { mutate: verifyToken } = useVerifyRegistrationToken({
    onSuccess: (data) => {
      setTokenValid(true);
      setUserInfo(data.user);
      setIsVerifying(false);
    },
    onError: (error: any) => {
      setShowError(true);
      setErrorMessage(
        error?.response?.data?.message ||
          "Link aktywacyjny jest nieprawidłowy lub wygasł"
      );
      setIsVerifying(false);
    },
  });

  const { mutate: activateAccount } = useActivateWithPassword({
    onSuccess: () => {
      navigate(ROUTE_ENUM.LOGIN);
    },
    onError: (error: any) => {
      setShowError(true);
      setErrorMessage(
        error?.response?.data?.message || "Błąd podczas aktywacji konta"
      );
    },
  });

  const validation = () =>
    Yup.object().shape({
      password: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .min(8, VALIDATION_ENUM.MIN_8),
      confirmPassword: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .oneOf([Yup.ref("password")], "Hasła muszą być identyczne"),
    });

  const initialValues = {
    password: "",
    confirmPassword: "",
  };

  const onSubmit = (values: typeof initialValues) => {
    if (token) {
      activateAccount({
        token,
        password: values.password,
      });
    }
  };

  useEffect(() => {
    if (authorizedUser) {
      checkIfLogged(authorizedUser, navigate);
    }
  }, [authorizedUser, navigate]);

  useEffect(() => {
    if (token) {
      verifyToken({ token });
    } else {
      setShowError(true);
      setErrorMessage("Brak tokenu aktywacyjnego");
      setIsVerifying(false);
    }
  }, [token, verifyToken]);

  if (isVerifying) {
    return (
      <LoginWrapper>
        <div className="login-admin">
          <h1>Weryfikacja...</h1>
          <p>Sprawdzanie tokenu aktywacyjnego...</p>
        </div>
      </LoginWrapper>
    );
  }

  if (!tokenValid) {
    return (
      <LoginWrapper>
        <div className="login-admin">
          <h1>Błąd aktywacji</h1>
          <p className="login-admin__error login-admin__error--show">
            {errorMessage}
            <SVG type={SVG_ENUM.CLOSE_CIRCLE} />
          </p>
          <Button
            label="Powrót do strony logowania"
            className="button--black login-wrapper__submit button--wide"
            styleType="primary"
            onClick={() => navigate(ROUTE_ENUM.LOGIN)}
          />
        </div>
      </LoginWrapper>
    );
  }

  return (
    <LoginWrapper>
      <div className="login-admin">
        <h1>Ustaw hasło</h1>
        {userInfo && (
          <div style={{ marginBottom: "20px" }}>
            <p>
              <strong>Imię i nazwisko:</strong> {userInfo.name}
            </p>
            <p>
              <strong>Email:</strong> {userInfo.email}
            </p>
          </div>
        )}
        <Formik
          initialValues={initialValues}
          onSubmit={(values) => onSubmit(values)}
          validationSchema={validation()}
          innerRef={formRef}
        >
          {({ errors, values, handleSubmit, setFieldValue }) => (
            <Form>
              <p
                className={`login-admin__error ${
                  showError ? "login-admin__error--show" : ""
                }`}
              >
                {errorMessage}
                <SVG type={SVG_ENUM.CLOSE_CIRCLE} />
              </p>
              <Field
                type="password"
                id="password"
                onFocus={() => setShowError(false)}
                name="password"
                label={"Hasło"}
                placeholder="Wprowadź hasło"
                as={Input}
              />

              <Field
                type="password"
                id="confirmPassword"
                onFocus={() => setShowError(false)}
                name="confirmPassword"
                label={"Powtórz hasło"}
                placeholder="Powtórz hasło"
                as={Input}
              />

              <Button
                label="Aktywuj konto"
                className="button--black login-wrapper__submit button--wide"
                styleType="primary"
                onClick={() => {}}
                type="submit"
              />
            </Form>
          )}
        </Formik>
      </div>
    </LoginWrapper>
  );
};

export default RegisterPage;
